import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { isDeadFormRoute, resolveFormRoute } from '../lib/field-forms';

export interface FieldForm {
  id: string;
  key: string;
  name: string;
  vertical: string;
  route: string;
  comingSoon?: boolean;
  /**
   * Why this row cannot be opened, in the words shown on the badge. Set by
   * `useAvailableForms` for a template with no fields; `comingSoon` is the
   * older, narrower flag for a route nobody has built and stays as it is.
   */
  disabledReason?: string;
  /** `/forms/available` only. Absent on the driver endpoint. */
  fieldCount?: number;
  category?: string | null;
}

export interface FormGroup {
  group: string;
  label?: string;
  vertical?: string;
  forms: FieldForm[];
}

interface ApiResponse {
  success: boolean;
  data: { groups: Array<{ label: string; vertical?: string; forms: FieldForm[] }> };
}

/**
 * The driver/rental-tech Forms tab. `/field/forms` resolves a `trucking_drivers`
 * row and 404s without one, which is why casing hands get a different door —
 * `useAvailableForms` — rather than this hook with a guard on it.
 */
export function useFieldForms() {
  return useQuery<FormGroup[]>({
    queryKey: ['field-forms'],
    queryFn: async () => {
      const r = await api.field.forms();
      const apiBody = r.data as ApiResponse;
      const rawGroups = apiBody?.data?.groups ?? [];
      return rawGroups.map((g) => ({
        group: g.label,
        label: g.label,
        vertical: g.vertical,
        forms: (g.forms ?? []).map((f) => ({
          ...f,
          // The column holds `/forms/dynamic/` with no id on it. Left raw, the
          // tap resolves to the catch-all and the crew lands back on Today.
          route: resolveFormRoute(f.route, f.id),
          // The API's own comingSoon wins where it sets one; this only adds the
          // flag to routes it still advertises as live.
          comingSoon: f.comingSoon || isDeadFormRoute(f.route) || undefined,
        })),
      }));
    },
    staleTime: 300_000,
  });
}
